"use client";

import React, { useEffect, useState } from "react";
import { Alert, Box, Button, IconButton, Collapse } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInBrowserIcon from "@mui/icons-material/OpenInBrowser";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import { useInAppBrowser } from "src/hooks/useInAppBrowser";
import { useOrder } from "@contexts/index";
import {
  androidChromeIntentUrl,
  handoffUrl,
  type InAppBrowserApp,
  type MobilePlatform,
} from "src/utils/in-app-browser";

/**
 * Nudge shown when the shop is opened inside a social app's in-app browser
 * (Facebook, Instagram, ...). Production data shows photo uploads fail as a
 * whole batch almost only there -- the WebView reclaims the picked files
 * mid-upload -- and the Facebook in-app browser loses 27.6% of card payments
 * after the redirect to Stripe, against ~11% in phone browsers. Getting the
 * customer into the real browser (Chrome/Safari) avoids both.
 *
 * Two forms:
 *  - "banner" (browsing pages): compact, dismissable for the rest of the tab.
 *  - "checkout" (the delivery step, above the pay button): inline and NOT
 *    dismissable, about paying, with the Apple Pay / Google Pay note.
 *
 * The way out depends on the phone: Android gets a button that hands the page
 * to Chrome (`intent://`); iOS cannot be handed off from a page, so it gets the
 * app's own menu steps. Both get "Copy link". The URL carries the cart (order
 * id + guest token), because the real browser shares no storage with the
 * WebView.
 *
 * Renders nothing outside an in-app browser, inside our own app's WebView, on
 * the server and on the first client paint (see useInAppBrowser).
 */
const APP_LABELS: Record<InAppBrowserApp, string> = {
  facebook: "Facebook",
  instagram: "Instagram",
  tiktok: "TikTok",
  snapchat: "Snapchat",
  pinterest: "Pinterest",
  linkedin: "LinkedIn",
  line: "LINE",
  other: "",
};

const DISMISSED_KEY = "fd-iab-notice-dismissed";

function readDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

function rememberDismissed(): void {
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, "1");
  } catch {
    // Storage blocked: the banner is simply back on the next full load.
  }
}

/** Clipboard first, then the old execCommand route WebViews still allow. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through: some WebViews expose the API and refuse it.
  }
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    area.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

export interface InAppBrowserNoticeProps {
  variant?: "banner" | "checkout";
  /**
   * Checkout only: cash on delivery is on offer for this order, so the wallet
   * note can say it works here too.
   */
  codOffered?: boolean;
}

const InAppBrowserNotice: React.FC<InAppBrowserNoticeProps> = ({
  variant = "banner",
  codOffered = false,
}) => {
  const t = useAppTranslate();
  const { app, platform } = useInAppBrowser();
  const [dismissed, setDismissed] = useState(false);
  const checkout = variant === "checkout";

  useEffect(() => {
    if (!checkout) setDismissed(readDismissed());
  }, [checkout]);

  if (!app || (!checkout && dismissed)) return null;

  const appName = APP_LABELS[app] || "";

  const message = checkout
    ? t(
        "inAppBrowser.checkoutWarning",
        { app: appName },
        `V aplikaciji ${appName} se plačilo pogosto prekine. Za zanesljivo plačilo odprite stran v brskalniku – vaše naročilo vas tam čaka.`,
      )
    : t(
        "inAppBrowser.uploadWarning",
        { app: appName },
        `Odprli ste stran v aplikaciji ${appName}, kjer nalaganje fotografij pogosto ne uspe. Za pravilno delovanje jo odprite v svojem brskalniku (npr. Chrome ali Safari).`,
      );
  const hint = howToFor(t, app, platform);
  const walletsNote = checkout
    ? codOffered
      ? t(
          "inAppBrowser.walletsNote",
          "Apple Pay in Google Pay delujeta le v brskalniku. Plačilo s kartico in po povzetju delujeta tudi tukaj.",
        )
      : t(
          "inAppBrowser.walletsNoteCardOnly",
          "Apple Pay in Google Pay delujeta le v brskalniku. Plačilo s kartico deluje tudi tukaj.",
        )
    : null;

  return (
    <Collapse in>
      <Box sx={checkout ? { mt: 2, mx: "auto", maxWidth: 600 } : { px: { xs: 1, sm: 2 }, pt: 1 }}>
        <Alert
          severity="warning"
          variant="outlined"
          icon={<OpenInBrowserIcon />}
          data-testid={`in-app-browser-notice-${variant}`}
          action={
            checkout ? undefined : (
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  rememberDismissed();
                  setDismissed(true);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            )
          }
          sx={{ alignItems: "flex-start", textAlign: "left" }}
        >
          <Box sx={{ fontWeight: 600, mb: 0.5 }}>{message}</Box>
          <Box sx={{ fontSize: "0.85rem", opacity: 0.85 }}>{hint}</Box>
          {walletsNote && (
            <Box sx={{ fontSize: "0.85rem", opacity: 0.85, mt: 0.5 }}>
              {walletsNote}
            </Box>
          )}
          <OpenInBrowserActions platform={platform} />
        </Alert>
      </Box>
    </Collapse>
  );
};

/**
 * The way out of the in-app browser: Android's hand-off to Chrome and, on
 * every phone, "Copy link". The URL carries the cart (handoffUrl).
 */
export function OpenInBrowserActions({ platform }: { platform: MobilePlatform }) {
  const t = useAppTranslate();
  const { mainOrderId, guestToken } = useOrder();
  const [copied, setCopied] = useState<"yes" | "no" | null>(null);
  const currentUrl = () =>
    handoffUrl(window.location.href, { orderId: mainOrderId, guestToken });
  const onCopy = async () => {
    setCopied((await copyText(currentUrl())) ? "yes" : "no");
  };
  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
        {platform === "android" && (
          <Button
            size="small"
            variant="contained"
            color="warning"
            startIcon={<OpenInBrowserIcon />}
            onClick={() => {
              window.location.href = androidChromeIntentUrl(currentUrl());
            }}
          >
            {t("inAppBrowser.openInBrowser", "Odpri v brskalniku")}
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<ContentCopyIcon />}
          onClick={onCopy}
        >
          {t("inAppBrowser.copyLink", "Kopiraj povezavo")}
        </Button>
      </Box>
      {copied === "yes" && (
        <Box sx={{ fontSize: "0.85rem", mt: 0.75 }} role="status">
          {t(
            "inAppBrowser.linkCopied",
            "Povezava je kopirana. Prilepite jo v brskalnik (Safari ali Chrome).",
          )}
        </Box>
      )}
      {copied === "no" && (
        <Box
          sx={{ fontSize: "0.75rem", mt: 0.75, wordBreak: "break-all", userSelect: "all" }}
        >
          {currentUrl()}
        </Box>
      )}
    </>
  );
}

/**
 * The app's own menu steps. Facebook and Instagram on iOS name the item
 * differently, so each gets its own sentence; Android and every other app get
 * the general one (on Android the button above is the main way out).
 */
export function howToFor(
  t: ReturnType<typeof useAppTranslate>,
  app: InAppBrowserApp,
  platform: MobilePlatform,
): string {
  if (platform === "ios" && app === "facebook") {
    return t(
      "inAppBrowser.howToIosFacebook",
      "Tapnite ··· in izberite »Odpri v zunanjem brskalniku«.",
    );
  }
  if (platform === "ios" && app === "instagram") {
    return t(
      "inAppBrowser.howToIosInstagram",
      "Tapnite ··· zgoraj desno in izberite »Odpri v sistemskem brskalniku«.",
    );
  }
  if (platform === "android") {
    return t(
      "inAppBrowser.howToAndroid",
      "Če se stran ne odpre, tapnite ⋮ zgoraj desno in izberite »Odpri v brskalniku«.",
    );
  }
  return t(
    "inAppBrowser.howTo",
    "Tapnite meni (⋮ ali ···) zgoraj in izberite »Odpri v brskalniku«.",
  );
}

export default InAppBrowserNotice;
